import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

/**
 * ResponsiveModal — Near-app adaptive modal component.
 *
 * Desktop (≥600px): MUI Dialog centralizado.
 * Mobile  (<600px): MUI Drawer de baixo com drag handle, estilo bottom-sheet.
 *
 * Props:
 *  - open (bool): se o modal está aberto
 *  - onClose (fn): callback para fechar
 *  - title (string|node): título do modal
 *  - children (node): conteúdo principal
 *  - actions (node): botões de ação (footer)
 *  - fullScreenOnMobile (bool): se true, drawer ocupa tela toda no mobile (default: false → half-screen)
 *  - maxWidth (string): maxWidth do Dialog no desktop ('xs'|'sm'|'md'|'lg') default 'sm'
 *  - disableBackdropClose (bool): se true, não fecha ao clicar fora
 *  - sx (object): estilos extras para o conteúdo
 */
export default function ResponsiveModal({
  open,
  onClose,
  title,
  children,
  actions,
  fullScreenOnMobile = false,
  maxWidth = "sm",
  disableBackdropClose = false,
  sx = {},
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleBackdropClose = (event, reason) => {
    if (disableBackdropClose && reason === "backdropClick") return;
    onClose?.();
  };

  // ===== MOBILE: Bottom Sheet via Drawer =====
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={handleBackdropClose}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: fullScreenOnMobile ? "95vh" : "70vh",
            minHeight: 200,
            display: "flex",
            flexDirection: "column",
            ...sx,
          },
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {/* Drag handle */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: 1,
            pb: 0.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 4,
              borderRadius: 2,
              bgcolor: "rgba(0,0,0,0.2)",
            }}
          />
        </Box>

        {/* Header */}
        {title && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              pb: 1,
              borderBottom: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontSize: 16 }}
            >
              {title}
            </Typography>
            <IconButton size="small" onClick={onClose} sx={{ ml: 1 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: 2,
            py: 2,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {children}
        </Box>

        {/* Actions */}
        {actions && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: "1px solid rgba(0,0,0,0.08)",
              display: "flex",
              gap: 1,
              justifyContent: "flex-end",
              flexShrink: 0,
            }}
          >
            {actions}
          </Box>
        )}
      </Drawer>
    );
  }

  // ===== DESKTOP: Dialog centralizado =====
  return (
    <Dialog
      open={open}
      onClose={handleBackdropClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          ...sx,
        },
      }}
    >
      {title && (
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 700,
            pb: 1,
          }}
        >
          {title}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
      )}
      <DialogContent sx={{ pt: title ? 1 : 2 }}>{children}</DialogContent>
      {actions && <DialogActions sx={{ px: 3, pb: 2 }}>{actions}</DialogActions>}
    </Dialog>
  );
}